import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { getCrmConfig } from './db/baseql/crm-ops.js';
import { type CsvLeadRow, parseLeadFile } from './lead-csv.js';
import { toast } from './toast.js';

export interface AddLeadsModalProps {
  campaignId: string;
  /** Campaign was originally sourced from Apollo — enables the top-up tab. */
  hasApolloSearch: boolean;
  open: boolean;
  onClose: () => void;
  /** Called after leads were actually added so the host can refresh its list. */
  onLeadsAdded: () => void | Promise<void>;
}

/**
 * "Add Leads" modal (CSV / Excel upload, HubSpot list import, Apollo top-up),
 * shared between the campaign wizard's Leads step (pre-launch / cool-down
 * editing) and the campaign detail page (paused campaigns — the wizard isn't
 * reachable for those). Extracted from campaign-detail.ts; the append and
 * apollo-topup endpoints generate messages for the new leads themselves.
 */
export const AddLeadsModal = stateful((props: AddLeadsModalProps) => {
  const { campaignId, hasApolloSearch, open, onClose, onLeadsAdded } = props;

  const [tab, setTab] = useState<'csv' | 'hubspot' | 'apollo'>('csv');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvParsed, setCsvParsed] = useState<CsvLeadRow[] | null>(null);
  const [hubspotAvailable, setHubspotAvailable] = useState(false);
  const [hubspotLists, setHubspotLists] = useState<Array<{
    listId: string;
    name: string;
    size?: number;
  }> | null>(null);
  const [hubspotLoadingLists, setHubspotLoadingLists] = useState(false);
  const [hubspotImporting, setHubspotImporting] = useState<string | null>(null);
  const [apolloTopupCount, setApolloTopupCount] = useState(20);

  // Reset transient state whenever the modal (re-)opens, and probe HubSpot
  // availability once so the tab can say "not connected" instead of erroring.
  useEffect(() => {
    if (!open) return;
    setTab('csv');
    setError(null);
    setResult(null);
    setCsvFileName(null);
    setCsvParsed(null);
    setHubspotLists(null);
    if (!hubspotAvailable) {
      void (async () => {
        try {
          const cfg = await getCrmConfig()({ hubspot: { enabled: 1 } });
          if (cfg?.hubspot?.enabled) setHubspotAvailable(true);
        } catch {
          /* ignore */
        }
      })();
    }
  }, [open]);

  const submitAppendLeads = async (source: 'csv' | 'hubspot', leads: Record<string, string>[]) => {
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/leads/append`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, leads }),
      });
      const data = await resp.json();
      if (!data.ok) {
        setError(data.error ?? 'Failed to add leads');
        return;
      }
      const parts: string[] = [`${data.added} added`];
      if (data.skippedDuplicates > 0) parts.push(`${data.skippedDuplicates} already in campaign`);
      if (data.skippedSuppressed > 0) parts.push(`${data.skippedSuppressed} on Do Not Contact list`);
      setResult(parts.join(' · '));
      setCsvParsed(null);
      setCsvFileName(null);
      await onLeadsAdded();
      if (data.added > 0)
        toast.success(`${data.added} lead${data.added === 1 ? '' : 's'} added — generating messages…`);
    } catch {
      setError('Request failed');
    }
    setBusy(false);
  };

  const submitApolloTopup = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/leads/apollo-topup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ additionalLeadCount: apolloTopupCount }),
      });
      const data = await resp.json();
      if (!data.ok) {
        setError(data.error ?? 'Failed to source leads');
        return;
      }
      if (data.message) {
        setResult(data.message);
      } else {
        const parts: string[] = [`${data.added} of ${data.requested} sourced`];
        if (data.shortfall > 0) {
          parts.push(
            `${data.shortfall} short — Apollo had no more matches with ${data.requiredContactData ?? 'the required contact data'}`,
          );
        }
        setResult(parts.join(' · '));
      }
      await onLeadsAdded();
      if (data.added > 0) {
        toast.success(`${data.added} lead${data.added === 1 ? '' : 's'} sourced from Apollo — generating messages…`);
      }
    } catch {
      setError('Request failed');
    }
    setBusy(false);
  };

  const handleFile = async (file: File) => {
    setError(null);
    setResult(null);
    const { leads, errors } = await parseLeadFile(file);
    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }
    setCsvFileName(file.name);
    setCsvParsed(leads);
  };

  const pickFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.tsv,.txt,.xlsx,.xls';
    input.style.display = 'none';
    const cleanup = () => input.remove();
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      cleanup();
      if (file) void handleFile(file);
    });
    input.addEventListener('cancel', cleanup);
    document.body.appendChild(input);
    input.click();
  };

  const openHubspotListPicker = async () => {
    setTab('hubspot');
    if (hubspotLists !== null) return;
    setHubspotLoadingLists(true);
    setError(null);
    try {
      const r = await fetch('/api/crm/hubspot/lists').then(x => x.json());
      if (!r?.ok) throw new Error(r?.error ?? 'Failed to load lists');
      setHubspotLists(r.lists ?? []);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load HubSpot lists');
      setHubspotLists([]);
    }
    setHubspotLoadingLists(false);
  };

  const importHubspotList = async (listId: string) => {
    setHubspotImporting(listId);
    setError(null);
    try {
      const r = await fetch(`/api/crm/hubspot/lists/${encodeURIComponent(listId)}/contacts?max=2000`).then(x =>
        x.json(),
      );
      if (!r?.ok) throw new Error(r?.error ?? 'Failed to import contacts');
      const contacts: Record<string, string>[] = (r.contacts ?? [])
        .filter((c: any) => c.email)
        .map((c: any) => ({
          firstName: c.firstName ?? '',
          lastName: c.lastName ?? '',
          email: c.email,
          company: c.company ?? '',
          jobTitle: c.jobTitle ?? '',
          linkedinUrl: c.linkedinUrl ?? '',
          industry: c.industry ?? '',
          crmContactId: c.crmContactId ?? '',
        }));
      if (contacts.length === 0) throw new Error('List contained no contacts with an email address');
      await submitAppendLeads('hubspot', contacts);
    } catch (e: any) {
      setError(e?.message ?? 'Import failed');
    }
    setHubspotImporting(null);
  };

  if (!open) return nothing;

  return html`
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click=${(e: MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="bg-base-100 flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl shadow-2xl">
        <div class="border-base-300 flex items-center justify-between border-b px-5 py-3">
          <div class="flex items-center gap-2">
            <span class="icon-[material-symbols--person-add] text-primary text-xl"></span>
            <h3 class="font-semibold">Add Leads</h3>
          </div>
          <button class="btn btn-ghost btn-sm btn-circle" @click=${onClose}>
            <span class="icon-[material-symbols--close]"></span>
          </button>
        </div>

        <div role="tablist" class="tabs tabs-bordered px-5 pt-3">
          <a
            role="tab"
            class="tab ${tab === 'csv' ? 'tab-active' : ''}"
            @click=${() => {
              setTab('csv');
              setError(null);
            }}
            >CSV / Excel</a
          >
          <a role="tab" class="tab ${tab === 'hubspot' ? 'tab-active' : ''}" @click=${openHubspotListPicker}
            >HubSpot</a
          >
          ${
            hasApolloSearch
              ? html`
                <a
                  role="tab"
                  class="tab ${tab === 'apollo' ? 'tab-active' : ''}"
                  @click=${() => {
                    setTab('apollo');
                    setError(null);
                    setResult(null);
                  }}
                  >Apollo</a
                >
              `
              : nothing
          }
        </div>

        <div class="overflow-y-auto p-5">
          ${
            error
              ? html`
                <div class="alert alert-error mb-3 text-sm whitespace-pre-wrap">
                  <span class="icon-[material-symbols--error]"></span>
                  <span>${error}</span>
                </div>
              `
              : nothing
          }
          ${
            result
              ? html`
                <div class="alert alert-success mb-3 text-sm">
                  <span class="icon-[material-symbols--check-circle]"></span>
                  <span>${result}</span>
                </div>
              `
              : nothing
          }
          ${
            tab === 'csv'
              ? html`
                <p class="text-base-content/60 mb-3 text-sm">
                  Upload a CSV or Excel file with new leads for this campaign. Each row needs at least an
                  email or a LinkedIn URL.
                </p>
                <button class="btn btn-outline btn-sm gap-1.5" @click=${pickFile}>
                  <span class="icon-[material-symbols--upload] text-base"></span>
                  Choose File
                </button>
                ${
                  csvFileName && csvParsed
                    ? html`
                      <div class="border-base-300 mt-3 flex items-center justify-between rounded-lg border p-3">
                        <div class="text-sm">
                          <span class="font-medium">${csvFileName}</span>
                          <span class="text-base-content/60"> — ${csvParsed.length} lead${
                            csvParsed.length === 1 ? '' : 's'
                          } parsed</span
                          >
                        </div>
                        <button
                          class="btn btn-primary btn-sm gap-1.5 ${busy ? 'btn-disabled' : ''}"
                          @click=${() => void submitAppendLeads('csv', csvParsed as unknown as Record<string, string>[])}
                        >
                          ${
                            busy
                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                              : html`<span class="icon-[material-symbols--add] text-base"></span>`
                          }
                          Add ${csvParsed.length} Lead${csvParsed.length === 1 ? '' : 's'}
                        </button>
                      </div>
                    `
                    : nothing
                }
              `
              : tab === 'hubspot'
                ? html`
                ${
                  !hubspotAvailable
                    ? html`<p class="text-base-content/60 py-4 text-center text-sm">
                      HubSpot is not connected for this account.
                    </p>`
                    : hubspotLoadingLists
                      ? html`<div class="text-base-content/60 flex items-center justify-center py-10">
                        <span class="loading loading-spinner loading-md"></span
                        ><span class="ml-2">Loading lists…</span>
                      </div>`
                      : (hubspotLists?.length ?? 0) === 0
                        ? html`<div class="text-base-content/60 py-8 text-center text-sm">
                          No lists found in this HubSpot portal.
                        </div>`
                        : html`<div class="divide-base-300 flex flex-col divide-y">
                          ${(hubspotLists ?? []).map(
                            l => html`
                              <div class="flex items-center justify-between gap-3 py-2.5">
                                <div class="min-w-0">
                                  <div class="truncate font-medium">${l.name}</div>
                                  <div class="text-base-content/50 text-xs">
                                    ${l.size !== undefined ? `${l.size} contacts` : 'size unknown'} · list ${l.listId}
                                  </div>
                                </div>
                                <button
                                  class="btn btn-warning btn-sm shrink-0 gap-1"
                                  ?disabled=${hubspotImporting !== null || busy}
                                  @click=${() => void importHubspotList(l.listId)}
                                >
                                  ${
                                    hubspotImporting === l.listId
                                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                                      : html`<span class="icon-[material-symbols--download] text-base"></span>`
                                  }
                                  Import
                                </button>
                              </div>
                            `,
                          )}
                        </div>`
                }
              `
                : html`
                  <p class="text-base-content/60 mb-3 text-sm">
                    Source more leads from Apollo using this campaign's original search criteria — new leads
                    are deduped against everyone already in the campaign, and only leads with the contact
                    data your steps need (email address / LinkedIn URL) count toward the number.
                  </p>
                  <div class="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      class="input input-bordered input-sm w-28"
                      .value=${String(apolloTopupCount)}
                      @input=${(e: Event) => {
                        const v = parseInt((e.target as HTMLInputElement).value, 10);
                        setApolloTopupCount(Number.isFinite(v) && v > 0 ? v : 1);
                      }}
                    />
                    <span class="text-base-content/60 text-sm">additional leads</span>
                    <button
                      class="btn btn-primary btn-sm gap-1.5 ${busy ? 'btn-disabled' : ''}"
                      @click=${() => void submitApolloTopup()}
                    >
                      ${
                        busy
                          ? html`<span class="loading loading-spinner loading-xs"></span>`
                          : html`<span class="icon-[material-symbols--travel-explore] text-base"></span>`
                      }
                      Source Leads
                    </button>
                  </div>
                `
          }
        </div>
      </div>
    </div>
  `;
});
